import { useCallback } from 'react'
import { toastTypeConfig } from '@/components/Common/Toast'
import { useStores } from '@/components/StoreProvider'

export type ToastType = keyof typeof toastTypeConfig

interface UseToastReturn {
  showToast: (message: string, type: ToastType, duration?: number) => void
  showSuccess: (message: string, duration?: number) => void
  showError: (message: string, duration?: number) => void
  showWarning: (message: string, duration?: number) => void
  showInfo: (message: string, duration?: number) => void
  clearAllToasts: () => void
}

export function useToast(): UseToastReturn {
  const { chatStore } = useStores()

  const showToast = useCallback(
    (message: string, type: ToastType, duration: number = 5000) => {
      chatStore.addToast(message, type, duration)
    },
    [chatStore],
  )

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'success', duration)
    },
    [showToast],
  )

  const showError = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'error', duration)
    },
    [showToast],
  )

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'warning', duration)
    },
    [showToast],
  )

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'info', duration)
    },
    [showToast],
  )

  const clearAllToasts = useCallback(() => {
    chatStore.clearAllToasts()
  }, [chatStore])

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAllToasts,
  }
}
